import {
  Package,
  Flower,
  ShoppingCart,
  Users,
  MessageCircle,
  Truck,
  Settings,
  Workflow,
  Car,
  Tags,
  UserCog,
  Store,
  Sparkles,
  Percent,
  Image,
  Clock,
  LayoutDashboard,
  Mail,
  FileEdit,
  ClipboardList,
  Palette,
  BarChart3,
  Banknote,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { UserRole } from '../components/data/orderWorkflowData'

// ============= Route Config Types =============

export interface RouteConfig {
  id: string
  path: string
  label: string
  icon: LucideIcon
  allowedRoles?: UserRole[]
  getLabel?: (role: UserRole) => string
  getIcon?: (role: UserRole) => LucideIcon
}

export interface MenuItemConfig {
  id: string
  path: string
  label: string
  icon: LucideIcon
  badge?: number
}

// ============= Menu Group Types (Phase 1) =============

/**
 * Menu group identifiers
 * Following Fowler's "Replace Magic String with Symbolic Constant"
 */
export type MenuGroupId = 'operations' | 'team' | 'catalog' | 'ai' | 'system'

/**
 * Subgroup identifiers within system group
 */
export type SystemSubGroupId = 'storefront'

/**
 * Subgroup configuration
 */
export interface MenuSubGroup {
  id: SystemSubGroupId
  label: string
  icon: LucideIcon
  routes: string[] // route IDs
}

/**
 * Menu group configuration
 * Following Fowler's "Introduce Parameter Object"
 */
export interface MenuGroup {
  id: MenuGroupId
  label: string
  icon: LucideIcon
  allowedRoles: UserRole[]
  defaultExpanded: boolean
  routes: string[] // route IDs
  subGroups?: MenuSubGroup[]
}

/**
 * Grouped menu item for rendering
 */
export interface GroupedMenuItem extends MenuItemConfig {
  groupId: MenuGroupId
  subGroupId?: SystemSubGroupId
}

/**
 * Menu group with resolved items for rendering
 */
export interface ResolvedMenuGroup {
  id: MenuGroupId
  label: string
  icon: LucideIcon
  items: MenuItemConfig[]
  subGroups?: {
    id: SystemSubGroupId
    label: string
    icon: LucideIcon
    items: MenuItemConfig[]
  }[]
  totalBadge: number
}

/**
 * Private routes collection
 * Do not export directly - use NavigationService instead
 */
const ROUTES: RouteConfig[] = [
  {
    id: 'dashboard',
    path: '/dashboard',
    label: 'İdarə paneli',
    icon: LayoutDashboard,
    allowedRoles: ['admin', 'dispatcher'],
  },
  {
    id: 'order-workflow',
    path: '/order-workflow',
    label: 'İş prosesi',
    icon: Workflow,
    getLabel: (role: UserRole) => (role === 'courier' ? 'Çatdırılmalarım' : 'İş prosesi'),
    getIcon: (role: UserRole) => (role === 'courier' ? Car : Workflow),
    allowedRoles: ['admin', 'dispatcher', 'florist', 'courier', 'support'],
  },
  {
    id: 'orders',
    path: '/orders',
    label: 'Bütün sifarişlər',
    icon: ShoppingCart,
    allowedRoles: ['admin', 'dispatcher', 'support'],
  },
  {
    id: 'flowers',
    path: '/flowers',
    label: 'Güllər',
    icon: Flower,
    allowedRoles: ['admin'],
  },
  {
    id: 'flowers-drafts',
    path: '/flowers/drafts',
    label: 'Gül qaralamalar',
    icon: FileEdit,
    allowedRoles: ['admin'],
  },
  {
    id: 'bouquets',
    path: '/bouquets',
    label: 'Buketlər',
    icon: Package,
    allowedRoles: ['admin'],
  },
  {
    id: 'categories',
    path: '/categories',
    label: 'Kateqoriyalar',
    icon: Tags,
    allowedRoles: ['admin'],
  },
  {
    id: 'requirements',
    path: '/requirements',
    label: 'Tələblər',
    icon: Package,
    allowedRoles: ['admin'],
  },
  {
    id: 'florists',
    path: '/florists',
    label: 'Floristlər',
    icon: Sparkles,
    allowedRoles: ['admin', 'dispatcher'],
  },
  {
    id: 'clients',
    path: '/clients',
    label: 'Müştərilər',
    icon: Users,
    allowedRoles: ['admin'],
  },
  {
    id: 'users',
    path: '/users',
    label: 'İstifadəçilər',
    icon: UserCog,
    allowedRoles: ['admin'],
  },
  {
    id: 'stores',
    path: '/stores',
    label: 'Mağazalar',
    icon: Store,
    allowedRoles: ['admin', 'dispatcher'],
  },
  {
    id: 'support',
    path: '/support',
    label: 'Dəstək',
    icon: MessageCircle,
    allowedRoles: ['admin', 'support'],
  },
  {
    id: 'couriers',
    path: '/couriers',
    label: 'Kuryerlər',
    icon: Truck,
    allowedRoles: ['admin', 'dispatcher'],
  },
  {
    id: 'promotions',
    path: '/promotions',
    label: 'Aksiyalar',
    icon: Percent,
    allowedRoles: ['admin'],
  },
  {
    id: 'hero-settings',
    path: '/hero-settings',
    label: 'Hero bölməsi',
    icon: Image,
    allowedRoles: ['admin'],
  },
  {
    id: 'home-layout',
    path: '/home-layout',
    label: 'Ana səhifə',
    icon: LayoutDashboard,
    allowedRoles: ['admin'],
  },
  {
    id: 'greeting-cards',
    path: `${import.meta.env.VITE_VCARDS_URL || 'http://localhost:5176'}/admin/themes`,
    label: 'Virtual açıqcalar',
    icon: Mail,
    allowedRoles: ['admin'],
  },
  {
    id: 'delivery-settings',
    path: '/delivery-settings',
    label: 'Çatdırılma',
    icon: Clock,
    allowedRoles: ['admin', 'dispatcher'],
  },
  {
    id: 'ai-settings',
    path: '/ai-settings',
    label: 'AI (GPT)',
    icon: Sparkles,
    allowedRoles: ['admin'],
  },
  {
    id: 'settings',
    path: '/settings',
    label: 'Parametrlər',
    icon: Settings,
    allowedRoles: ['admin'],
  },
  {
    id: 'behavior-analytics',
    path: '/behavior-analytics',
    label: 'Davranış',
    icon: BarChart3,
    allowedRoles: ['admin'],
  },
  {
    id: 'payments-report',
    path: '/payments-report',
    label: 'BirBank ödənişlər',
    icon: Banknote,
    allowedRoles: ['admin'],
  },
]

// ============= Menu Groups Configuration (Phase 1) =============

/**
 * Menu groups configuration
 *
 * Following Fowler's "Introduce Parameter Object" and "Replace Conditional with Polymorphism"
 * Each group defines:
 * - Domain boundary (operations, team, catalog, etc.)
 * - Allowed roles for the entire group
 * - Route IDs that belong to this group
 * - Optional subgroups for nested organization
 *
 * Note: Florist does NOT see Catalog group (only through API if needed)
 */
const MENU_GROUPS: MenuGroup[] = [
  {
    id: 'operations',
    label: 'Əməliyyatlar',
    icon: ClipboardList,
    allowedRoles: ['admin', 'dispatcher', 'florist', 'courier', 'support'],
    defaultExpanded: true,
    routes: ['dashboard', 'order-workflow', 'orders'],
  },
  {
    id: 'team',
    label: 'Komanda',
    icon: Users,
    allowedRoles: ['admin', 'dispatcher', 'support'],
    defaultExpanded: false,
    routes: ['florists', 'couriers', 'stores', 'support'],
  },
  {
    id: 'catalog',
    label: 'Kataloq',
    icon: Package,
    allowedRoles: ['admin'],
    defaultExpanded: false,
    routes: ['flowers', 'flowers-drafts', 'bouquets', 'categories', 'requirements'],
  },
  {
    id: 'ai',
    label: 'AI',
    icon: Sparkles,
    allowedRoles: ['admin'],
    defaultExpanded: false,
    routes: ['ai-settings'],
  },
  {
    id: 'system',
    label: 'Sistem',
    icon: Settings,
    allowedRoles: ['admin', 'dispatcher'],
    defaultExpanded: false,
    routes: ['users', 'clients', 'delivery-settings', 'settings', 'behavior-analytics', 'payments-report'],
    subGroups: [
      {
        id: 'storefront',
        label: 'Vitrin',
        icon: Palette,
        routes: ['hero-settings', 'home-layout', 'promotions', 'greeting-cards'],
      },
    ],
  },
]

/**
 * Navigation Service
 *
 * Following Fowler's "Encapsulate Collection" principle.
 * Provides controlled access to routes collection with role-based filtering.
 *
 * Benefits:
 * - Single source of truth for navigation logic
 * - Prevents direct mutation of routes
 * - Centralizes role-based access control
 * - Type-safe route resolution
 */
export class NavigationService {
  /**
   * Get menu items for a specific user role
   *
   * @param userRole - User role to filter routes
   * @returns Array of menu items allowed for the role
   */
  static getMenuItemsForRole(userRole: UserRole): MenuItemConfig[] {
    return ROUTES.filter(route => {
      if (!route.allowedRoles) return true
      return route.allowedRoles.includes(userRole)
    }).map(route => ({
      id: route.id,
      path: route.path,
      label: route.getLabel ? route.getLabel(userRole) : route.label,
      icon: route.getIcon ? route.getIcon(userRole) : route.icon,
    }))
  }

  /**
   * Get all routes filtered by user role
   *
   * @param userRole - User role to filter routes
   * @returns Array of route configs allowed for the role
   */
  static getRoutesForRole(userRole: UserRole): RouteConfig[] {
    return ROUTES.filter(route => {
      if (!route.allowedRoles) return true
      return route.allowedRoles.includes(userRole)
    })
  }

  /**
   * Get route config by ID
   *
   * @param id - Route ID
   * @returns Route config or undefined if not found
   */
  static getRouteById(id: string): RouteConfig | undefined {
    return ROUTES.find(route => route.id === id)
  }

  /**
   * Get route config by path
   *
   * @param path - Route path
   * @returns Route config or undefined if not found
   */
  static getRouteByPath(path: string): RouteConfig | undefined {
    return ROUTES.find(route => route.path === path)
  }

  /**
   * Get route ID from path
   *
   * @param path - Route path
   * @returns Route ID or 'orders' as fallback
   */
  static getRouteIdFromPath(path: string): string {
    const route = this.getRouteByPath(path)
    return route?.id || 'orders'
  }

  /**
   * Check if user role can access specific route
   *
   * @param routeId - Route ID to check
   * @param userRole - User role
   * @returns True if role has access to the route
   */
  static canAccessRoute(routeId: string, userRole: UserRole): boolean {
    const route = this.getRouteById(routeId)
    if (!route) return false
    if (!route.allowedRoles) return true
    return route.allowedRoles.includes(userRole)
  }

  /**
   * Get landing page path for user role
   *
   * @param userRole - User role
   * @returns Landing page path
   */
  static getLandingPageForRole(userRole: UserRole): string {
    // Courier and florist go directly to their workspace
    if (userRole === 'courier' || userRole === 'florist') {
      return '/order-workflow'
    }

    // Managers and admin go to dashboard
    return '/dashboard'
  }

  /**
   * Get total number of routes
   * Useful for analytics/debugging
   *
   * @returns Total route count
   */
  static getRouteCount(): number {
    return ROUTES.length
  }

  // ============= Grouped Navigation Methods (Phase 1) =============

  /**
   * Check if a menu group is visible for a specific role
   *
   * Following Fowler's "Replace Conditional with Polymorphism" -
   * group visibility is determined by configuration, not scattered conditionals
   *
   * @param groupId - Menu group ID
   * @param userRole - User role
   * @returns True if the group has at least one visible route for the role
   */
  static isGroupVisibleForRole(groupId: MenuGroupId, userRole: UserRole): boolean {
    const group = MENU_GROUPS.find(g => g.id === groupId)
    if (!group) return false

    // First check: role must be in group's allowedRoles
    if (!group.allowedRoles.includes(userRole)) return false

    // Second check: at least one route in the group must be accessible
    const hasVisibleRoute = group.routes.some(routeId => this.canAccessRoute(routeId, userRole))

    // Also check subgroups
    const hasVisibleSubgroupRoute = group.subGroups?.some(subGroup =>
      subGroup.routes.some(routeId => this.canAccessRoute(routeId, userRole))
    )

    return hasVisibleRoute || !!hasVisibleSubgroupRoute
  }

  /**
   * Get grouped menu items for a specific user role
   *
   * Following Fowler's "Encapsulate Collection" - returns immutable resolved groups
   * with all route details resolved and filtered by role
   *
   * @param userRole - User role to filter
   * @param badgeCounts - Optional badge counts keyed by route ID
   * @returns Array of resolved menu groups with items
   */
  static getGroupedMenuItemsForRole(
    userRole: UserRole,
    badgeCounts?: Record<string, number | undefined>
  ): ResolvedMenuGroup[] {
    const result: ResolvedMenuGroup[] = []

    for (const group of MENU_GROUPS) {
      if (!this.isGroupVisibleForRole(group.id, userRole)) continue

      // Resolve main routes
      const items = this.resolveRouteItems(group.routes, userRole, badgeCounts)

      // Resolve subgroups
      const resolvedSubGroups: ResolvedMenuGroup['subGroups'] = []
      if (group.subGroups) {
        for (const subGroup of group.subGroups) {
          const subItems = this.resolveRouteItems(subGroup.routes, userRole, badgeCounts)
          if (subItems.length > 0) {
            resolvedSubGroups.push({
              id: subGroup.id,
              label: subGroup.label,
              icon: subGroup.icon,
              items: subItems,
            })
          }
        }
      }

      // Calculate total badge for the group
      const itemsBadge = items.reduce((sum, item) => sum + (item.badge || 0), 0)
      const subGroupsBadge = resolvedSubGroups.reduce(
        (sum, sg) => sum + sg.items.reduce((s, item) => s + (item.badge || 0), 0),
        0
      )

      const resolvedGroup: ResolvedMenuGroup = {
        id: group.id,
        label: group.label,
        icon: group.icon,
        items,
        totalBadge: itemsBadge + subGroupsBadge,
      }

      if (resolvedSubGroups.length > 0) {
        resolvedGroup.subGroups = resolvedSubGroups
      }

      result.push(resolvedGroup)
    }

    return result
  }

  /**
   * Helper: resolve route IDs to menu items
   * Following Fowler's "Extract Method"
   */
  private static resolveRouteItems(
    routeIds: string[],
    userRole: UserRole,
    badgeCounts?: Record<string, number | undefined>
  ): MenuItemConfig[] {
    const result: MenuItemConfig[] = []

    for (const routeId of routeIds) {
      const route = this.getRouteById(routeId)
      if (!route) continue
      if (!this.canAccessRoute(routeId, userRole)) continue

      const item: MenuItemConfig = {
        id: route.id,
        path: route.path,
        label: route.getLabel ? route.getLabel(userRole) : route.label,
        icon: route.getIcon ? route.getIcon(userRole) : route.icon,
      }

      const badge = badgeCounts?.[routeId]
      if (badge !== undefined) {
        item.badge = badge
      }

      result.push(item)
    }

    return result
  }

  /**
   * Get default expanded state for groups
   *
   * @returns Record of groupId -> defaultExpanded
   */
  static getDefaultExpandedGroups(): Record<MenuGroupId, boolean> {
    return MENU_GROUPS.reduce(
      (acc, group) => {
        acc[group.id] = group.defaultExpanded
        return acc
      },
      {} as Record<MenuGroupId, boolean>
    )
  }

  /**
   * Find which group contains a specific route
   *
   * @param routeId - Route ID to find
   * @returns Object with groupId and optional subGroupId, or null if not found
   */
  static findGroupForRoute(
    routeId: string
  ): { groupId: MenuGroupId; subGroupId?: SystemSubGroupId } | null {
    for (const group of MENU_GROUPS) {
      // Check main routes
      if (group.routes.includes(routeId)) {
        return { groupId: group.id }
      }

      // Check subgroups
      if (group.subGroups) {
        for (const subGroup of group.subGroups) {
          if (subGroup.routes.includes(routeId)) {
            return { groupId: group.id, subGroupId: subGroup.id }
          }
        }
      }
    }
    return null
  }

  /**
   * Get all menu group configurations
   * Useful for debugging and testing
   *
   * @returns Copy of menu groups array
   */
  static getMenuGroups(): readonly MenuGroup[] {
    return MENU_GROUPS
  }
}

// ============= DEPRECATED EXPORTS (for backward compatibility) =============
// Use NavigationService instead

/**
 * @deprecated Use NavigationService.getMenuItemsForRole() instead
 */
export function getMenuItemsForRole(userRole: UserRole): MenuItemConfig[] {
  return NavigationService.getMenuItemsForRole(userRole)
}

/**
 * @deprecated Use NavigationService.getRouteById() instead
 */
export function getRouteById(id: string): RouteConfig | undefined {
  return NavigationService.getRouteById(id)
}

/**
 * @deprecated Use NavigationService.getRouteByPath() instead
 */
export function getRouteByPath(path: string): RouteConfig | undefined {
  return NavigationService.getRouteByPath(path)
}

/**
 * @deprecated Use NavigationService.getRouteIdFromPath() instead
 */
export function getRouteIdFromPath(path: string): string {
  return NavigationService.getRouteIdFromPath(path)
}

